import { progressCircle } from '@repo/themes/styled-system/recipes';
import { ComponentProps, HTMLProps } from 'react';
import {
  ProgressCircleProvider,
  ProgressCircleProviderProps,
  useProgressCircle,
} from '../contexts/ProgressCircleContext';

const SIZE = 45;
const STROKE_WIDTH = 5;
const RADIUS = (SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const ProgressCircleRoot = ({
  children,
  value,
  max,
}: ProgressCircleProviderProps) => (
  <ProgressCircleProvider value={value} max={max}>
    <div className={progressCircle().root}>{children}</div>
  </ProgressCircleProvider>
);

const ProgressCircleCircle = (props: HTMLProps<'svg'>) => {
  return <svg className={progressCircle().track}>{props.children}</svg>;
};

const ProgressCircleTrack = () => (
  <circle
    stroke='{#e0e0e0}'
    fill='none'
    cx={SIZE / 2}
    cy={SIZE / 2}
    r={RADIUS}
    strokeWidth={STROKE_WIDTH}
  />
);

const ProgressCircleRange = () => {
  const { percentage = 0, variant } = useProgressCircle();

  const displayPercentage = variant === 'dimmed' ? 100 : percentage;

  const offset = CIRCUMFERENCE * (1 - displayPercentage / 100);

  return (
    <circle
      className={progressCircle({ variant }).range}
      cx={SIZE / 2}
      cy={SIZE / 2}
      r={RADIUS}
      strokeWidth={STROKE_WIDTH}
      fill='none'
      style={{ transition: 'stroke-dashoffset 0.3s ease' }}
      transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
      strokeDasharray={CIRCUMFERENCE}
      strokeDashoffset={offset}
    />
  );
};

const ProgressCircleValueText = ({ ...props }: ComponentProps<'span'>) => {
  const { percentage = 0, variant } = useProgressCircle();
  const text = variant === 'dimmed' ? 'N/A' : `${Math.round(percentage)}%`;

  return (
    <span
      {...props}
      className={progressCircle({ variant }).valueText}
      style={{ fontSize: 'small' }}
    >
      {text}
    </span>
  );
};

const ProgressCircle = ({ value, max }: { value: number; max: number }) => (
  <ProgressCircleRoot value={value} max={max}>
    <ProgressCircleCircle>
      <ProgressCircleTrack />
      <ProgressCircleRange />
    </ProgressCircleCircle>
    <ProgressCircleValueText />
  </ProgressCircleRoot>
);

ProgressCircle.displayName = 'ProgressCircle';

export default Object.assign(ProgressCircle, {
  Root: ProgressCircleRoot,
  Circle: ProgressCircleCircle,
  Track: ProgressCircleTrack,
  Range: ProgressCircleRange,
  ValueText: ProgressCircleValueText,
});
